import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom'
import { LanguageProvider } from './store/contexts/LanguageContext'
import { UserProvider } from './store/contexts/UserContext'
import { DreamProvider } from './store/contexts/DreamContext'
import { GoalProvider } from './store/contexts/GoalContext'
import { PlanProvider } from './store/contexts/PlanContext'
import { ItemProvider } from './store/contexts/ItemContext'
import { ActionProvider } from './store/contexts/ActionContext'
import { AccountProvider } from './store/contexts/AccountContext'
import { CategoryProvider } from './store/contexts/CategoryContext'
import { CoachProvider } from './store/contexts/CoachContext'
import { DashboardProvider } from './store/contexts/DashboardContext'
import { MasterProvider } from './store/contexts/MasterContext'
import { ToastProvider } from './store/contexts/ToastContext'
import { ConfirmProvider } from './store/contexts/ConfirmContext'
import { LoadingProvider } from './store/contexts/LoadingContext'
import MainLayout from './layouts/MainLayout'
import RouteChangeHandler from './components/common/RouteChangeHandler/RouteChangeHandler'
import AppVisibilityRefresh from './components/common/AppVisibilityRefresh/AppVisibilityRefresh'
import ProtectedRoute from './components/common/ProtectedRoute/ProtectedRoute'
import Dashboard from './pages/Dashboard'
import Dreams from './pages/Dreams'
import GoalList from './pages/GoalList'
import GoalDetail from './pages/GoalDetail'
import Calendar from './pages/Calendar'
import Coaching from './pages/Coaching'
import UserManagement from './pages/UserManagement'
import Accounts from './pages/Accounts'
import Login from './pages/Login'
import OAuthCallback from './pages/OAuthCallback'
import Settings from './pages/Settings'

import Admin from './pages/Admin'
import Terms from './pages/Terms'
import Privacy from './pages/Privacy'
import NotFound from './pages/NotFound'
import ErrorPage from './pages/ErrorPage'
import { useEffect } from 'react'

/** 체크리스트 전용 URL 접근 시 목표 상세로 리다이렉트 (아이템은 모달로만 표시) */
function RedirectChecklistToGoal() {
  const { id, goalId } = useParams<{ id: string; goalId: string }>()
  return <Navigate to={id && goalId ? `/dreams/${id}/goals/${goalId}` : '/dreams'} replace />
}

function App() {
  // WebView back 처리
  useEffect(() => {
    const handleBackButton = (_e: PopStateEvent) => {
      // WebView에서 뒤로가기 버튼 처리
      // React Router가 자동으로 처리하므로 여기서는 추가 로직만 필요시 구현
    }

    window.addEventListener('popstate', handleBackButton)

    // WebView(앱) 환경이면 body에 클래스 추가 → 모달 등 fixed 요소가 잘리지 않도록 CSS 대응
    if (window.ReactNativeWebView) {
      document.documentElement.classList.add('env-webview')
      document.body.classList.add('env-webview')
    }

    // WebView 환경 감지 및 메시지 처리
    let handleMessage: ((event: MessageEvent) => void) | undefined
    if (window.ReactNativeWebView) {
      handleMessage = (event: MessageEvent) => {
        try {
          const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data
          if (data === 'back' || data?.type === 'BACK') {
            window.history.back()
          }
          if (data?.type === 'PURCHASE_CALLBACK') {
            const { token } = data
            if (typeof (window as any).purchaseCallback === 'function') {
              ;(window as any).purchaseCallback(token || null)
            }
          }
          if (data?.type === 'REWARDED_AD_CALLBACK') {
            const { platform, error: rewardedErr } = data
            if (typeof (window as any).rewardedAdCallback === 'function') {
              ;(window as any).rewardedAdCallback(platform || null, rewardedErr ?? null)
            }
          }
        } catch {
          if (event.data === 'back') {
            window.history.back()
          }
        }
      }
      window.addEventListener('message', handleMessage)
    }

    return () => {
      window.removeEventListener('popstate', handleBackButton)
      if (handleMessage) {
        window.removeEventListener('message', handleMessage)
      }
      document.documentElement.classList.remove('env-webview')
      document.body.classList.remove('env-webview')
    }
  }, [])

  return (
    <BrowserRouter
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <LanguageProvider>
        <LoadingProvider>
          <ToastProvider>
            <UserProvider>
            <DreamProvider>
              <GoalProvider>
                <PlanProvider>
                  <ItemProvider>
                    <ActionProvider>
                      <AccountProvider>
                        <CategoryProvider>
                          <CoachProvider>
                            <DashboardProvider>
                                <MasterProvider>
                                  <ConfirmProvider>
                                    <RouteChangeHandler />
                                    <AppVisibilityRefresh />
                                    <Routes>
                                      {/* 인증 불필요: Login, OAuthCallback, UserManagement */}
                                      <Route path="/login" element={<Login />} errorElement={<ErrorPage />} />
                                      <Route path="/auth/callback" element={<OAuthCallback />} errorElement={<ErrorPage />} />
                                      <Route path="/login/oauth2/code/google" element={<OAuthCallback />} errorElement={<ErrorPage />} />
                                      <Route path="/login/oauth2/code/apple" element={<OAuthCallback />} errorElement={<ErrorPage />} />
                                      <Route
                                        path="/user"
                                        element={
                                          <ProtectedRoute>
                                            <MainLayout>
                                              <UserManagement />
                                            </MainLayout>
                                          </ProtectedRoute>
                                        }
                                      />
                                      
                                      {/* 인증 필요: 나머지 모든 페이지 */}
                                      <Route
                                        path="/"
                                        element={
                                          <ProtectedRoute>
                                            <Navigate to="/dreams" replace />
                                          </ProtectedRoute>
                                        }
                                      />
                                      <Route
                                        path="/dreams"
                                        element={
                                          <ProtectedRoute>
                                            <MainLayout>
                                              <Dreams />
                                            </MainLayout>
                                          </ProtectedRoute>
                                        }
                                      />
                                      <Route
                                        path="/dreams/:id"
                                        element={
                                          <ProtectedRoute>
                                            <MainLayout>
                                              <GoalList />
                                            </MainLayout>
                                          </ProtectedRoute>
                                        }
                                      />
                                      <Route
                                        path="/dreams/:id/goals/:goalId"
                                        element={
                                          <ProtectedRoute>
                                            <MainLayout>
                                              <GoalDetail />
                                            </MainLayout>
                                          </ProtectedRoute>
                                        }
                                      />
                                      <Route
                                        path="/dreams/:id/goals/:goalId/plans/:planId"
                                        element={
                                          <ProtectedRoute>
                                            <RedirectChecklistToGoal />
                                          </ProtectedRoute>
                                        }
                                      />
                                      <Route
                                        path="/calendar"
                                        element={
                                          <ProtectedRoute>
                                            <MainLayout>
                                              <Calendar />
                                            </MainLayout>
                                          </ProtectedRoute>
                                        }
                                      />
                                      <Route
                                        path="/coaching"
                                        element={
                                          <ProtectedRoute>
                                            <MainLayout>
                                              <Coaching />
                                            </MainLayout>
                                          </ProtectedRoute>
                                        }
                                      />
                                      <Route
                                        path="/dashboard"
                                        element={
                                          <ProtectedRoute>
                                            <MainLayout>
                                              <Dashboard />
                                            </MainLayout>
                                          </ProtectedRoute>
                                        }
                                      />
                                      <Route
                                        path="/accounts"
                                        element={
                                          <ProtectedRoute>
                                            <MainLayout>
                                              <Accounts />
                                            </MainLayout>
                                          </ProtectedRoute>
                                        }
                                      />
                                      <Route
                                        path="/settings"
                                        element={
                                          <ProtectedRoute>
                                            <MainLayout>
                                              <Settings />
                                            </MainLayout>
                                          </ProtectedRoute>
                                        }
                                      />

                                      <Route
                                        path="/admin"
                                        element={
                                          <ProtectedRoute>
                                            <MainLayout>
                                              <Admin />
                                            </MainLayout>
                                          </ProtectedRoute>
                                        }
                                      />
                                      <Route
                                        path="/settings/terms"
                                        element={
                                          <ProtectedRoute>
                                            <MainLayout>
                                              <Terms />
                                            </MainLayout>
                                          </ProtectedRoute>
                                        }
                                      />
                                      <Route
                                        path="/settings/privacy"
                                        element={
                                          <ProtectedRoute>
                                            <MainLayout>
                                              <Privacy />
                                            </MainLayout>
                                          </ProtectedRoute>
                                        }
                                      />
                                      <Route path="*" element={<NotFound />} />
                                    </Routes>
                                  </ConfirmProvider>
                                </MasterProvider>
                            </DashboardProvider>
                          </CoachProvider>
                        </CategoryProvider>
                      </AccountProvider>
                    </ActionProvider>
                  </ItemProvider>
                </PlanProvider>
              </GoalProvider>
            </DreamProvider>
            </UserProvider>
          </ToastProvider>
        </LoadingProvider>
      </LanguageProvider>
    </BrowserRouter>
  )
}

export default App
